export const SUPPORTED_REQUEST_LOCALES = [
  "en",
  "es",
  "fr",
  "de",
  "it",
  "pt",
  "zh",
  "ja",
  "ko",
  "ru",
  "ar",
  "hi",
] as const;

export type SupportedLocale = (typeof SUPPORTED_REQUEST_LOCALES)[number];

export const DEFAULT_REQUEST_LOCALE: SupportedLocale = "en";

const supportedLocales = new Set<string>(SUPPORTED_REQUEST_LOCALES);

/** The supported locale a tag selects ("pt_BR" → "pt"), or null when none does. */
export function supportedLocale(value: string | null | undefined): SupportedLocale | null {
  const normalized = String(value || "").trim().replaceAll("_", "-").toLowerCase();
  if (!normalized) return null;
  if (supportedLocales.has(normalized)) return normalized as SupportedLocale;

  const baseLocale = normalized.split("-")[0];
  return supportedLocales.has(baseLocale) ? baseLocale as SupportedLocale : null;
}

function acceptedLocales(header: string | null | undefined): string[] {
  return String(header || "")
    .split(",")
    .map((entry, index) => {
      const [locale, ...parameters] = entry.trim().split(";");
      const qualityParameter = parameters.find((parameter) => parameter.trim().startsWith("q="));
      const parsedQuality = qualityParameter
        ? Number.parseFloat(qualityParameter.trim().slice(2))
        : 1;
      return {
        locale,
        quality: Number.isFinite(parsedQuality) ? parsedQuality : 0,
        index,
      };
    })
    .filter(({ locale, quality }) => Boolean(locale) && quality > 0)
    .sort((left, right) => right.quality - left.quality || left.index - right.index)
    .map(({ locale }) => locale);
}

export function resolveRequestLocale({
  cookieLocale,
  acceptLanguage,
}: {
  cookieLocale?: string | null;
  acceptLanguage?: string | null;
}): SupportedLocale {
  for (const candidate of [cookieLocale, ...acceptedLocales(acceptLanguage)]) {
    const locale = supportedLocale(candidate);
    if (locale) return locale;
  }

  return DEFAULT_REQUEST_LOCALE;
}
